import humanizeString from 'humanize-string';
import jsYaml from 'js-yaml';
import * as Yup from 'yup';

import { capitalizeText } from './text';

declare module 'yup' {
  interface StringSchema<
    TType extends Yup.Maybe<string> = string | undefined,
    TContext = Yup.AnyObject,
    TDefault = undefined,
    TFlags extends Yup.Flags = '',
  > extends Yup.Schema<TType, TContext, TDefault, TFlags> {
    slug(): StringSchema<TType, TContext, TDefault, TFlags>;
    yaml(): StringSchema<TType, TContext, TDefault, TFlags>;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface MixedSchema<TType extends Yup.Maybe<{}>> {
    email(): MixedSchema<TType>;
    max(minValue: number): MixedSchema<TType>;
    min(maxValue: number): MixedSchema<TType>;
    required(): MixedSchema<TType>;
    url(): MixedSchema<TType>;
  }

  interface ArraySchema<
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Yup.Flags = '',
  > extends Yup.Schema<TIn, TContext, TDefault, TFlags> {
    unique(
      message?: string,
      mapper?: (a: any) => any,
    ): ArraySchema<TIn, TContext, TDefault, TFlags>;
  }
}

const emailRegex =
  /^[\w!#$%&'*+./=?^`{|}~-]+@[\dA-Za-z](?:[\dA-Za-z-]{0,61}[\dA-Za-z])?(?:\.[\dA-Za-z](?:[\dA-Za-z-]{0,61}[\dA-Za-z])?)*$/;

Yup.addMethod(Yup.string, 'email', function (errorMessage) {
  return this.test(`email`, errorMessage, function (value) {
    if (!value) {
      return true;
    }

    return (
      emailRegex.test(value || '') || this.createError({ message: errorMessage, path: this.path })
    );
  });
});

Yup.addMethod(Yup.string, 'yaml', function (errorMessage) {
  return this.test(`yaml`, errorMessage, function (value) {
    try {
      jsYaml.load(value || '');

      return true;
    } catch {
      return this.createError({ message: errorMessage, path: this.path });
    }
  });
});

Yup.addMethod(Yup.string, 'slug', function (errorMessage) {
  return this.test(`slug`, errorMessage, function (value = '') {
    if (!value) {
      return true;
    }

    return (
      (/^[a-z](-?[\da-z]+)+$/.test(value) && value.length <= 255) ||
      this.createError({ message: errorMessage, path: this.path })
    );
  });
});

Yup.addMethod(Yup.mixed, 'required', function () {
  return this.test(`required`, '', function (value) {
    if (typeof value === 'string') {
      try {
        Yup.string().required().validateSync(value, { context: this.options.context });

        return true;
      } catch (error) {
        return this.createError({ message: error.message, path: this.path });
      }
    }

    return value !== undefined && value !== null
      ? true
      : this.createError({ message: '', path: this.path });
  });
});

Yup.addMethod(Yup.mixed, 'oneOf', function (allowedValues: any[]) {
  return this.test(`oneOf`, '', function (value) {
    if (!value) {
      return true;
    }

    try {
      if (typeof value === 'string') {
        Yup.string().oneOf(allowedValues).validateSync(value, { context: this.options.context });
      } else if (typeof value === 'number') {
        Yup.number().oneOf(allowedValues).validateSync(value, { context: this.options.context });
      } else if (typeof value === 'boolean') {
        Yup.boolean().oneOf(allowedValues).validateSync(value, { context: this.options.context });
      } else if (Array.isArray(value)) {
        Yup.array().oneOf(allowedValues).validateSync(value, { context: this.options.context });
      }

      return true;
    } catch (error) {
      return this.createError({ message: error.message, path: this.path });
    }
  });
});

Yup.addMethod(Yup.mixed, 'min', function (minValue: number) {
  return this.test(`min`, '', function (value) {
    if (['number', 'string'].includes(typeof value)) {
      try {
        if (typeof value === 'string' && value.trim().length > 0) {
          Yup.string().min(minValue).validateSync(value, { context: this.options.context });
        } else if (typeof value === 'number') {
          Yup.number().min(minValue).validateSync(value, { context: this.options.context });
        }

        return true;
      } catch (error) {
        return this.createError({
          message: error.message,
          params: {
            min: minValue,
          },
          path: this.path,
        });
      }
    }

    return true;
  });
});

Yup.addMethod(Yup.mixed, 'max', function (maxValue: number) {
  return this.test(`max`, '', function (value) {
    if (['number', 'string'].includes(typeof value)) {
      try {
        if (typeof value === 'string' && value.trim().length > 0) {
          Yup.string().max(maxValue).validateSync(value, { context: this.options.context });
        } else if (typeof value === 'number') {
          Yup.number().max(maxValue).validateSync(value, { context: this.options.context });
        }

        return true;
      } catch (error) {
        return this.createError({
          message: error.message,
          params: {
            max: maxValue,
          },
          path: this.path,
        });
      }
    }

    return true;
  });
});

Yup.addMethod(Yup.mixed, 'email', function () {
  return this.test(`email`, '', function (value) {
    if (typeof value === 'string' && value.trim()) {
      return emailRegex.test(value || '') || this.createError({ message: '', path: this.path });
    }

    return true;
  });
});

Yup.addMethod(Yup.mixed, 'url', function () {
  return this.test(`url`, '', function (value) {
    if (typeof value === 'string' && value.trim()) {
      try {
        Yup.string().url().validateSync(value, { context: this.options.context });

        return true;
      } catch (error) {
        return this.createError({ message: error.message, path: this.path });
      }
    }

    return true;
  });
});

Yup.addMethod(Yup.array, 'unique', function (message, mapper = (a: any) => a) {
  return this.test('unique', message, function (list = []) {
    return list.length === new Set(list.map((element) => mapper(element))).size;
  });
});

export function convertStringToSlug(value: string) {
  let slug = value
    .toLowerCase()
    .replaceAll(/[^\da-z-]+/g, '-')
    .replaceAll(/-{2,}/g, '-')
    .replaceAll(/^-+|-+$/g, '');

  if (!/^[a-z]/.test(slug)) {
    slug = `a${slug}`;
  }

  return slug;
}

export function getYupValidationResolver(validationSchema: Yup.AnyObjectSchema) {
  return async (data: any) => {
    try {
      const values = await validationSchema.validate(data, {
        abortEarly: false,
      });

      return {
        errors: {},
        values,
      };
    } catch (error: unknown) {
      const err = error as Yup.ValidationError;

      return {
        errors: Object.fromEntries(
          err.inner.map((currentError) => {
            return [
              currentError.path,
              {
                message: getFieldErrorMessage(currentError),
                type: currentError.type ?? 'validation',
              },
            ];
          }),
        ),
        values: {},
      };
    }
  };
}

Yup.setLocale({
  array: {
    length: ({ length }) => `{{fieldLabel}} must have exactly ${length} items`,
    max: ({ max }) => `{{fieldLabel}} must have at most ${max} items`,
    min: ({ min }) => `{{fieldLabel}} must have at least ${min} items`,
  },
  boolean: {
    isValue: '{{fieldLabel}} must be a true or false',
  },
  date: {
    max: ({ max }) => `{{fieldLabel}} must be before ${max}`,
    min: ({ min }) => `{{fieldLabel}} must be after ${min}`,
  },
  mixed: {
    default: '{{fieldLabel}} is invalid',
    defined: '{{fieldLabel}} must be defined',
    notNull: '{{fieldLabel}} is required',
    notOneOf: ({ values }) =>
      `{{fieldLabel}} cannot be one of: ${Array.isArray(values) ? values.join(', ') : values}`,
    notType: ({ type }) => `{{fieldLabel}} must be a ${type}`,
    oneOf: ({ values }) =>
      `{{fieldLabel}} must be one of: ${Array.isArray(values) ? values.join(', ') : values}`,
    required: '{{fieldLabel}} is required',
  },
  number: {
    integer: '{{fieldLabel}} must be an integer',
    lessThan: ({ less }) => `{{fieldLabel}} must be less than ${less}`,
    max: ({ max }) => `{{fieldLabel}} must be at most ${max}`,
    min: ({ min }) => `{{fieldLabel}} must be at least ${min}`,
    moreThan: ({ more }) => `{{fieldLabel}} must be more than ${more}`,
    negative: '{{fieldLabel}} must be a negative number',
    positive: '{{fieldLabel}} must be a positive number',
  },
  object: {
    noUnknown: '{{fieldLabel}} contains unknown keys that are not allowed',
  },
  string: {
    email: '{{fieldLabel}} is invalid email',
    length: ({ length }) => `{{fieldLabel}} must be exactly ${length} characters`,
    lowercase: '{{fieldLabel}} must be in lowercase',
    matches: '{{fieldLabel}} does not match the required pattern',
    max: ({ max }) => `{{fieldLabel}} must be at most ${max} characters`,
    min: ({ min }) => `{{fieldLabel}} must be at least ${min} characters`,
    trim: '{{fieldLabel}} must not have leading or trailing spaces',
    uppercase: '{{fieldLabel}} must be in uppercase',
    url: '{{fieldLabel}} is invalid URL',
    uuid: '{{fieldLabel}} is invalid UUID',
  },
});

function getFieldErrorMessage(error: Yup.ValidationError): string {
  let fieldLabel =
    (error.params?.label as string) ||
    humanizeString(error.path?.split('.').pop() || '') ||
    'value';
  fieldLabel = capitalizeText(fieldLabel);
  let errorMessage = (error.message || '').replaceAll('{{fieldLabel}}', fieldLabel);
  errorMessage = capitalizeText(errorMessage);

  if (error.type === 'required' || error.type === 'optionality') {
    return errorMessage || `${fieldLabel} cannot be empty`;
  }

  if (error.type === 'oneOf') {
    return `${fieldLabel} must be one of the following values: ${error.params?.values}`;
  }

  if (error.type === 'yaml') {
    return `${fieldLabel} must be a valid YAML`;
  }

  if (error.type === 'slug') {
    return `${fieldLabel} must consist of a lower case letter, followed by alphanumeric segments separated by single dashes`;
  }

  if (error.type === 'email') {
    return `${fieldLabel} must be a valid email address`;
  }

  if (error.type === 'typeError' && error.params?.type === 'number') {
    return errorMessage || `${fieldLabel} must be a valid number`;
  }

  if (error.type === 'integer') {
    return errorMessage || `${fieldLabel} must be a valid integer`;
  }

  if (!error.params) {
    return errorMessage;
  }

  if (
    error.type === 'min' &&
    typeof error.params.value === 'number' &&
    typeof error.params.min === 'number'
  ) {
    return errorMessage || `${fieldLabel} must be more than or equal to ${error.params.min}`;
  }

  if (
    error.type === 'max' &&
    typeof error.params.value === 'number' &&
    typeof error.params.max === 'number'
  ) {
    return errorMessage || `${fieldLabel} must be less than or equal to ${error.params.max}`;
  }

  if (
    error.type === 'max' &&
    typeof error.params.value === 'string' &&
    typeof error.params.max === 'number'
  ) {
    return errorMessage || `${fieldLabel} must contain at most ${error.params.max} characters`;
  }

  if (
    error.type === 'min' &&
    typeof error.params.value === 'string' &&
    typeof error.params.min === 'number'
  ) {
    return errorMessage || `${fieldLabel} must contain at least ${error.params.min} characters`;
  }

  return errorMessage;
}

export type YupInferType<T extends Yup.ISchema<any, any>> = Yup.InferType<T>;

export type YupObjectSchema<T extends Yup.AnyObject> = Yup.ObjectSchema<T>;

export const yup = Yup;
